import type { LexiconEntry } from './lexicon-schema.js'

/**
 * Pitch Trainer Phase 2 lexicon entries (plan `docs/superpowers/plans/2026-10-19-pitch-trainer-phase2-plan.md`
 * M4 step 4.4). Every `copy` and every `tagLabel` Bowie wrote is his, verbatim (relayed by Brian
 * 2026-10-19; `vocal-phrase-on-pitch` from his #2361 review), and locked by
 * `lexicon-vocal-phase2-entries.test.ts`. Two later edits from the #2491 reviews: the out-of-range tag is
 * Joni's spec §5 wording ("wider than your range"), and the outline copy writes 1–3–5–3–1 with en dashes.
 *
 * Drafted by Les for Joni to confirm, because Bowie's copy does not include them:
 * - every `displayName`, `displayNameShort` and `pedagogicalLevel`;
 * - the `tagLabel` of the three exercise entries (`vocal-ex-*`), from Joni's own tags where she wrote them;
 * - the `copy` of the three difficulty reasons (`vocal-difficulty-*`), whose tags are Bowie's;
 * - all of `vocal-difficulty-fast` but its tag, which is Joni's §2 "Fast notes" (added with M5's picker).
 *
 * Tokens, as in Phase 1: `{{cents}}`, `{{semitones}}`, `{{count}}` are unsigned numbers (the direction
 * is in the id); `{{distance}}` is "a semitone" or "2 semitones"; `{{from}}` / `{{to}}` / `{{low}}` /
 * `{{high}}` are spelled note names; `{{key}}` / `{{songKey}}` are key names; `{{interval}}` and
 * `{{span}}` are full interval names, compound included ("major 6th", "major 10th"); `{{aInterval}}` and
 * `{{aSpan}}` are the same with their article ("an octave", "a major 6th"), for copy that needs one.
 */
export const VOCAL_PHASE2_LEXICON_ENTRIES: LexiconEntry[] = [
  {
    id: 'vocal-drift-sag-under',
    category: 'vocal',
    displayName: 'Started on pitch, sagged',
    displayNameShort: 'Sagged',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'You started in tune and slowly slid lower.',
      intermediate: 'Started on pitch and sagged about {{cents}} cents by the end.',
      advanced: 'Progressive drift to −{{cents}} ¢ by the last note; intervals individually intact.',
    },
    tagLabel: { beginner: 'slid low', intermediate: 'sagged', advanced: '−{{cents}} ¢ sag' },
  },
  {
    id: 'vocal-drift-sag-over',
    category: 'vocal',
    displayName: 'Started on pitch, crept sharp',
    displayNameShort: 'Crept sharp',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'You started in tune and slowly crept higher.',
      intermediate: 'Started on pitch and crept about {{cents}} cents sharp by the end.',
      advanced: 'Progressive drift to +{{cents}} ¢ by the last note; intervals individually intact.',
    },
    tagLabel: { beginner: 'crept high', intermediate: 'crept sharp', advanced: '+{{cents}} ¢ creep' },
  },
  {
    id: 'vocal-leap-narrow',
    category: 'vocal',
    displayName: 'Leap sung too small',
    displayNameShort: 'Leap narrow',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'The big jump from {{from}} to {{to}} came out too small.',
      intermediate: 'The leap {{from}}→{{to}} ({{aInterval}}) was {{cents}} cents narrow.',
      advanced: 'Largest interval error: {{interval}} {{from}}–{{to}}, compressed by {{cents}} ¢.',
    },
    tagLabel: { beginner: 'jump too small', intermediate: 'leap narrow', advanced: '−{{cents}} ¢ interval' },
  },
  {
    id: 'vocal-leap-wide',
    category: 'vocal',
    displayName: 'Leap sung too big',
    displayNameShort: 'Leap wide',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'The big jump from {{from}} to {{to}} came out too big.',
      intermediate: 'The leap {{from}}→{{to}} ({{aInterval}}) was {{cents}} cents wide.',
      advanced: 'Largest interval error: {{interval}} {{from}}–{{to}}, stretched by {{cents}} ¢.',
    },
    tagLabel: { beginner: 'jump too big', intermediate: 'leap wide', advanced: '+{{cents}} ¢ interval' },
  },
  {
    id: 'vocal-original-key-under',
    category: 'vocal',
    displayName: 'Sung in the original key',
    displayNameShort: 'Original key',
    pedagogicalLevel: 2,
    copy: {
      beginner: "You sang it in the song's original key — fine, but we'd moved it to {{key}} to suit your voice.",
      intermediate: 'You sang the original key ({{songKey}}), {{distance}} lower than the practice key.',
      advanced: 'Sung at the source key: −{{semitones}} st against the {{key}} reference; intervals intact.',
    },
    tagLabel: { beginner: 'original key', intermediate: 'original key', advanced: 'source key' },
  },
  {
    id: 'vocal-original-key-over',
    category: 'vocal',
    displayName: 'Sung in the original key',
    displayNameShort: 'Original key',
    pedagogicalLevel: 2,
    copy: {
      beginner: "You sang it in the song's original key — fine, but we'd moved it to {{key}} to suit your voice.",
      intermediate: 'You sang the original key ({{songKey}}), {{distance}} higher than the practice key.',
      advanced: 'Sung at the source key: +{{semitones}} st against the {{key}} reference; intervals intact.',
    },
    tagLabel: { beginner: 'original key', intermediate: 'original key', advanced: 'source key' },
  },
  {
    id: 'vocal-phrase-too-long',
    category: 'vocal',
    displayName: 'Too long to hold at once',
    displayNameShort: 'Too long',
    pedagogicalLevel: 1,
    copy: {
      beginner: "That's a lot of notes to remember — try a shorter piece.",
      intermediate: '{{count}} notes missed — that may be too much to hold at once. Try half.',
      advanced: '≥30 % of notes missed: likely memory load, not pitch. Split it.',
    },
    tagLabel: { beginner: 'too long', intermediate: 'too long', advanced: 'memory load' },
  },
  {
    id: 'vocal-phrase-out-of-range',
    category: 'vocal',
    displayName: 'Wider than your range',
    displayNameShort: 'Too wide',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'This part goes higher and lower than your voice — pick a shorter piece.',
      intermediate: 'This phrase spans {{aSpan}}, wider than your range ({{low}}–{{high}}).',
      advanced: 'Ambitus {{span}} exceeds calibrated range {{low}}–{{high}}; no transposition fits.',
    },
    tagLabel: { beginner: 'wider than your range', intermediate: 'wider than your range', advanced: 'exceeds range' },
  },
  {
    id: 'vocal-phrase-on-pitch',
    category: 'vocal',
    displayName: 'Every note on pitch',
    displayNameShort: 'On pitch',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Every note landed.',
      intermediate: 'Every note on pitch, every interval intact.',
      advanced: 'All notes within ±20 ¢; no missed or extra notes.',
    },
    tagLabel: { beginner: 'on pitch', intermediate: 'on pitch', advanced: 'clean' },
  },
  {
    id: 'vocal-ex-song-phrase',
    category: 'vocal',
    displayName: 'A phrase from your song',
    displayNameShort: 'Song phrase',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'A piece of your song to sing back.',
      intermediate: 'A phrase from your song — {{count}} notes, in your range.',
      advanced: 'Song excerpt: {{count}} pitch events, transposed by octave into the calibrated tessitura.',
    },
    tagLabel: { beginner: 'from your song', intermediate: 'song phrase', advanced: 'excerpt' },
  },
  {
    id: 'vocal-ex-guide-tones',
    category: 'vocal',
    displayName: 'Guide tones',
    displayNameShort: 'Guide tones',
    pedagogicalLevel: 2,
    copy: {
      beginner: "This song has no written tune, so here's a simple line through its chords.",
      intermediate: 'No melody written yet — a guide-tone line: one note per chord, moving by the smallest step.',
      advanced: 'Guide-tone line: 3rds and 7ths, voice-led by smallest interval, one per chord change.',
    },
    tagLabel: { beginner: 'one note per chord', intermediate: 'one note per chord', advanced: 'guide tones' },
  },
  {
    id: 'vocal-ex-chord-outline',
    category: 'vocal',
    displayName: 'Chord outline',
    displayNameShort: 'Outline',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Sing up and down each chord.',
      intermediate: 'Each chord outlined 1–3–5–3–1, in order.',
      advanced: 'Chord-tone arpeggiation, 1–3–5–3–1 per change.',
    },
    tagLabel: { beginner: 'up and down each chord', intermediate: '1–3–5–3–1', advanced: '1–3–5–3–1' },
  },
  {
    id: 'vocal-difficulty-leap',
    category: 'vocal',
    displayName: 'Has a big leap',
    displayNameShort: 'Big leap',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'This one has a big jump in it.',
      intermediate: 'Includes a leap of {{aInterval}}.',
      advanced: 'Largest leap: {{interval}}.',
    },
    tagLabel: { beginner: 'big jump', intermediate: 'leap of {{aInterval}}', advanced: '{{interval}} leap' },
  },
  {
    id: 'vocal-difficulty-chromatic',
    category: 'vocal',
    displayName: 'Has notes outside the key',
    displayNameShort: 'Outside the key',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'This one has notes outside the key.',
      intermediate: 'Includes chromatic notes.',
      advanced: '{{count}} chromatic notes.',
    },
    tagLabel: { beginner: 'notes outside the key', intermediate: 'chromatic notes', advanced: '{{count}} chromatic' },
  },
  {
    id: 'vocal-difficulty-fast',
    category: 'vocal',
    displayName: 'Has very fast notes',
    displayNameShort: 'Fast notes',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Some notes in this one go by very quickly, even slowed down.',
      intermediate: 'Some notes stay under 200 ms even at the slowest playback.',
      advanced: 'Shortest event < 200 ms at 0.67×.',
    },
    tagLabel: { beginner: 'fast notes', intermediate: 'fast notes', advanced: 'fast notes' },
  },
  {
    id: 'vocal-difficulty-span',
    category: 'vocal',
    displayName: 'Covers a wide range',
    displayNameShort: 'Wide',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'This one covers a lot of your voice.',
      intermediate: 'Spans {{aSpan}}.',
      advanced: 'Ambitus {{span}}.',
    },
    tagLabel: { beginner: 'wide', intermediate: 'spans {{aSpan}}', advanced: 'ambitus {{span}}' },
  },
]
